import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Loader2, MessageSquare, Calendar, Shield } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VKB_LOGO_URL = "https://customer-assets.emergentagent.com/job_ingenieur-agency/artifacts/7oos7e3e_VKBayern_logo.svg.png";
const DQS_LOGO_URL = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/b0frwk4w_3xp0oxvy_medium_DQS_en.png";
const MANSFELD_IMAGE = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/8u6zz0by_490df1d7-396a-450c-8096-d69de69384f5.JPG";
// Team photo
const MANSFELD_TEAM = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/v6u41d5v_ChatGPT%20Image%2026.%20Ma%CC%88rz%202026%2C%2014_15_58.png";

const OERAG_LOGO_URL = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/z1vg2anp_Oerag_RS_rot_HKS13_450.jpg";
const UKV_LOGO_URL = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/sbfyzkwd_UKV_Logo_sRGB.svg.png";
const FEUERSOZIETAET_LOGO_URL = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/7xpcu6ij_FSB_Logo.png.webp";

export default function KontaktPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    privacy_accepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, privacy_accepted: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = formData.name.trim();
    const email = formData.email.trim();
    const message = formData.message.trim();

    if (!name || !email || !message) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    if (name.length < 2) {
      toast.error("Bitte geben Sie Ihren vollständigen Namen ein");
      return;
    }

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailValid) {
      toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein");
      return;
    }

    if (message.length < 10) {
      toast.error("Ihre Nachricht ist zu kurz. Bitte geben Sie mindestens 10 Zeichen ein.");
      return;
    }

    if (!formData.privacy_accepted) {
      toast.error("Bitte akzeptieren Sie die Datenschutzerklärung");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name,
        email,
        phone: formData.phone.trim(),
        message,
        privacy_accepted: formData.privacy_accepted,
      });
      setIsSubmitted(true);
      toast.success("Ihre Anfrage wurde erfolgreich gesendet!");
    } catch (error) {
      console.error("Contact form error:", error);
      const detail = error?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error("Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.");
      } else {
        toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: Calendar, text: "Flexible Terminvereinbarung" },
    { icon: MessageSquare, text: "Persönliche Beratung" },
    { icon: Shield, text: "Unverbindlich & kostenlos" }
  ];

  if (isSubmitted) {
    return (
      <div data-testid="kontakt-page" className="pt-8">
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#003B70] to-[#002855]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Kontakt
              </h1>
              <p className="text-xl text-white/80">
                Wir freuen uns auf Ihre Nachricht.
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Vielen Dank für Ihre Anfrage!
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Wir haben Ihre Nachricht erhalten und werden uns in Kürze bei Ihnen melden.
            </p>
            <Link to="/">
              <Button className="bg-[#003B70] hover:bg-[#002855] text-white px-8">
                Zurück zur Startseite
              </Button>
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="kontakt-page" className="pt-8">
      {/* Hero Section */}
      <section className="relative py-10 md:py-14 bg-gradient-to-br from-[#003B70] to-[#002855] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 50% 50%, rgba(255,255,255,0.3) 1px, transparent 1px)`,
            backgroundSize: '30px 30px'
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sky-300 font-semibold text-sm uppercase tracking-wider">Kontakt</span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
                Lassen Sie uns sprechen
              </h1>
              <p className="text-xl text-white/80 leading-relaxed">
                Haben Sie Fragen oder wünschen eine Beratung? 
                Ich freue mich auf Ihre Nachricht.
              </p>
              <div className="flex flex-wrap gap-6 mt-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 text-white/90">
                    <benefit.icon className="h-5 w-5 text-sky-300" />
                    <span className="text-sm">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-center mt-4 lg:mt-0">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-sky-400/20 to-blue-600/20 rounded-2xl blur-3xl" />
                <div className="relative w-64 h-64 rounded-2xl overflow-hidden border-4 border-white/20 shadow-2xl">
                  <img 
                    src={MANSFELD_TEAM}
                    alt="Jens-Oliver Mansfeld am Telefon"
                    className="w-full h-full object-cover object-[30%_center]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Jens-Oliver Mansfeld
              </h2>
              <p className="text-lg text-[#003B70] font-medium mb-8">
                Fachagentur für Architekten und Ingenieure
              </p>
              
              <div className="space-y-6 mb-10">
                <a 
                  href="tel:+493033937950"
                  className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors group"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#003B70] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Telefon</p>
                    <p className="text-xl font-semibold text-slate-900 group-hover:text-[#003B70] transition-colors">
                      +49 30 33937950
                    </p>
                  </div>
                </a>
                
                <a 
                  href="mailto:info@mansfeld-versicherungen.de"
                  className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors group"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#003B70] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">E-Mail</p>
                    <p className="text-lg font-semibold text-slate-900 group-hover:text-[#003B70] transition-colors break-all">
                      info@mansfeld-versicherungen.de
                    </p>
                  </div>
                </a>
                
                <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50">
                  <div className="w-14 h-14 rounded-xl bg-[#003B70] flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Adresse</p>
                    <p className="text-lg font-semibold text-slate-900">
                      Fredericiastraße 6<br />
                      14059 Berlin
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50">
                  <div className="w-14 h-14 rounded-xl bg-[#003B70] flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Erreichbarkeit</p>
                    <p className="text-lg font-semibold text-slate-900">
                      Mo – Fr: 09:00 – 18:00 Uhr
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust Logos */}
              <div className="pt-8 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-5 font-medium">Partner & Zertifizierungen</p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center justify-center bg-slate-50 rounded-xl p-4">
                    <img 
                      src={VKB_LOGO_URL}
                      alt="Versicherungskammer Bayern"
                      className="h-24 w-auto"
                    />
                  </div>
                  <div className="flex items-center justify-center bg-slate-50 rounded-xl p-4">
                    <img 
                      src={DQS_LOGO_URL}
                      alt="DQS Zertifiziert"
                      className="h-24 w-auto"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="flex items-center justify-center bg-slate-50 rounded-lg p-3">
                    <img 
                      src={UKV_LOGO_URL}
                      alt="UKV"
                      className="h-16 w-auto"
                    />
                  </div>
                  <div className="flex items-center justify-center bg-slate-50 rounded-lg p-3">
                    <img 
                      src={FEUERSOZIETAET_LOGO_URL}
                      alt="Feuersozietät"
                      className="h-16 w-auto"
                    />
                  </div>
                  <div className="flex items-center justify-center bg-slate-50 rounded-lg p-3">
                    <img 
                      src={OERAG_LOGO_URL}
                      alt="ÖRAG Rechtsschutz"
                      className="h-16 w-auto"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 md:p-10 shadow-lg">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Kontakt aufnehmen
                </h3>
                <p className="text-slate-600 mb-8">
                  Füllen Sie das Formular aus und ich melde mich bei Ihnen.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 font-medium">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Ihr Name"
                      className="mt-2 bg-white border-slate-200 focus:border-[#003B70] focus:ring-[#003B70] h-12"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      E-Mail <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-email-input"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="ihre@email.de"
                      className="mt-2 bg-white border-slate-200 focus:border-[#003B70] focus:ring-[#003B70] h-12"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 font-medium">
                      Telefon <span className="text-slate-400 font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      data-testid="contact-phone-input"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+49 ..."
                      className="mt-2 bg-white border-slate-200 focus:border-[#003B70] focus:ring-[#003B70] h-12"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-slate-700 font-medium">
                      Nachricht <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Wie können wir Ihnen helfen?"
                      className="mt-2 bg-white border-slate-200 focus:border-[#003B70] focus:ring-[#003B70] min-h-[150px]"
                      required
                    />
                  </div>
                  
                  <div className="flex items-start gap-3 p-4 bg-white rounded-lg">
                    <Checkbox
                      id="privacy"
                      data-testid="contact-privacy-checkbox"
                      checked={formData.privacy_accepted}
                      onCheckedChange={handleCheckboxChange}
                      className="mt-1"
                    />
                    <Label htmlFor="privacy" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                      Ich habe die{" "}
                      <Link to="/datenschutz" className="text-[#003B70] hover:underline font-medium">
                        Datenschutzerklärung
                      </Link>{" "}
                      gelesen und bin mit der Verarbeitung meiner Daten einverstanden.
                      <span className="text-red-500"> *</span>
                    </Label>
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={isSubmitting}
                    className="w-full bg-[#003B70] hover:bg-[#002855] text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Anfrage senden
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
